import { Component, OnInit } from '@angular/core';
import { RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AuthService } from '../service/auth/auth.service';
import { LoggerService } from '../service/utils/logger.service';
import { QuestionFlagResponse, QuestionSearchResponse, RestService } from '../service/rest/rest.service';
import {
  QuestionResultsListComponent,
  QuestionResultsListItem
} from '../components/question-results-list/question-results-list.component';
import { QuestionDetailModalComponent } from '../components/question-detail-modal/question-detail-modal.component';

interface FlagOption {
  id: number;
  name: string;
}

@Component({
  selector: 'app-question-flags',
  standalone: true,
  imports: [RouterLink, CommonModule, FormsModule, QuestionResultsListComponent, QuestionDetailModalComponent],
  templateUrl: './question-flags.component.html',
  styleUrl: './question-flags.component.css'
})
export class QuestionFlagsComponent implements OnInit {
  flags: FlagOption[] = [];
  flagSelection: Record<number, boolean> = {};
  withNotes: boolean = false;

  isLoadingFlags = false;
  isSearching = false;
  hasSearched = false;

  results: QuestionResultsListItem[] = [];
  resultDisplayHtmlById: Record<number, string> = {};
  resultBadgesById: Record<number, string[]> = {};

  // Detail modal
  detailOpen: boolean = false;
  detailQuestionId: number | null = null;
  detailQuestionHtml: string | null = null;
  detailAnswersHtml: Array<string | null> = [];
  detailCorrectAnswer: string | null = null;

  private rawById: Record<number, QuestionSearchResponse> = {};

  constructor(
    private restService: RestService,
    private authService: AuthService
  ) {}

  async ngOnInit() {
    await this.loadFlags();
  }

  openDetail(questionId: number) {
    this.detailQuestionId = questionId;
    const r = this.rawById[questionId];
    this.detailQuestionHtml = (r?.questionHtml || null) as any;
    // The backend returns answer1..answer4 on this response type.
    this.detailAnswersHtml = [r?.answer1 || null, r?.answer2 || null, r?.answer3 || null, r?.answer4 || null];
    this.detailCorrectAnswer = ((r as any)?.correctAnswer || null) as any;
    this.detailOpen = true;
  }

  closeDetail() {
    this.detailOpen = false;
    this.detailQuestionId = null;
    this.detailQuestionHtml = null;
    this.detailAnswersHtml = [];
    this.detailCorrectAnswer = null;
  }

  get selectedFlagIds(): number[] {
    return this.flags
      .filter(f => this.flagSelection[f.id] === true)
      .map(f => f.id);
  }

  get canSearch(): boolean {
    const hasSelectedFlags = this.selectedFlagIds.length > 0;
    const canSearchWithNotesOnly = this.withNotes === true;
    // Requirement: disabled if no flags there. Allow notes-only search, though.
    if (this.flags.length === 0 && !canSearchWithNotesOnly) return false;
    return hasSelectedFlags || canSearchWithNotesOnly;
  }

  async loadFlags() {
    this.isLoadingFlags = true;
    try {
      const user = this.authService.getUser();
      const sessionId = user?.sid;
      if (!sessionId) return;

      const flags = await this.restService.getAvailableQuestionFlags(sessionId);
      this.flags = (flags || []).map((f: QuestionFlagResponse) => ({
        id: f.id,
        name: f.id === 4 ? 'Hidden' : (f.name || '')
      }));

      // Ensure selection map contains all ids (default unselected).
      const next: Record<number, boolean> = {};
      for (const f of this.flags) next[f.id] = this.flagSelection[f.id] === true;

      // UX: if nothing was selected yet, default-select all available filters.
      if (this.flags.length > 0 && !Object.values(next).some(v => v === true)) {
        for (const f of this.flags) next[f.id] = true;
      }
      this.flagSelection = next;

      // Load results immediately when flags are ready
      await this.onSearch();
    } catch (e) {
      LoggerService.error('Failed to load question flags:', e);
      this.flags = [];
      this.flagSelection = {};
    } finally {
      this.isLoadingFlags = false;
    }
  }

  onFilterChange() {
    this.onSearch();
  }

  async reloadFlags() {
    await this.loadFlags();
  }

  async onSearch() {
    if (!this.canSearch || this.isSearching) return;

    this.isSearching = true;
    this.hasSearched = true;
    this.results = [];
    this.resultDisplayHtmlById = {};
    this.resultBadgesById = {};
    this.rawById = {};

    try {
      const user = this.authService.getUser();
      const sessionId = user?.sid;
      if (!sessionId) return;

      const response = await this.restService.searchFlaggedQuestions(sessionId, {
        flagIds: this.selectedFlagIds,
        withNotes: this.withNotes
      });

      const rows = (response || []) as QuestionSearchResponse[];
      this.results = rows.map(r => ({ id: r.id, subjectName: r.subjectName }));
      for (const r of rows) this.rawById[r.id] = r;

      const nameByFlagId: Record<number, string> = {};
      for (const f of this.flags) nameByFlagId[f.id] = f.name;

      for (const r of rows) {
        this.resultDisplayHtmlById[r.id] = this.stripHtml(r.questionHtml || '');

        // Show "why it matched" based on current search filters.
        const reasons: string[] = [];
        const selected = new Set(this.selectedFlagIds);

        const flagId = (r.userFlagId ?? null) as number | null;
        const hide = Number(r.userHide ?? 0) === 1;
        const hasNotes = r.userHasNotes === true;

        if (flagId != null && selected.has(flagId)) {
          reasons.push(nameByFlagId[flagId] || `Flag ${flagId}`);
        }
        if (selected.has(4) && hide) {
          reasons.push('Hidden');
        }
        if (this.withNotes && hasNotes) {
          reasons.push('Notes');
        }

        if (reasons.length > 0) {
          this.resultBadgesById[r.id] = reasons;
        }
      }
    } catch (e) {
      LoggerService.error('Flagged questions search failed:', e);
    } finally {
      this.isSearching = false;
    }
  }

  private stripHtml(html: string): string {
    if (!html) return '';
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }
}



